import { useEffect, useMemo, useRef } from 'react';

import { createPortal } from 'react-dom';
import { Field, Form, useForm } from 'react-final-form';
import Geocode from 'react-geocode';
import OutsideClickHandler from 'react-outside-click-handler';
import { useSelector } from 'react-redux';

import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Loader from 'components/shared/Loader';
import MenuActionsHelper from 'components/shared/MenuActionsHelper';
import { PAGE_TYPE_AREA, PAGE_TYPE_PROPERTY } from 'constants/index';
import { useEscAction } from 'hooks/useEscAction';
import { useSnackbar } from 'hooks/useSnackbar';
import { accountIdSelector, pageTypeOptionsSelector } from 'store/user/selectors';
import api from 'utils/axios';
import { capitalizeAllWordsForInput, generateHandleString } from 'utils/common';
import { getCoordsByAddress } from 'utils/maps';
import { ERROR_MESSAGES } from 'utils/yup';

import { EDITING_BLOCK_TYPES, getNormalizedPositionStyles, useEditorContext } from '../context';
import { useFunctions } from './functions';
import styles from './index.module.css';

function EnterHandler() {
  const form = useForm();
  useEffect(() => {
    const listener = (event) => {
      event.stopPropagation();
      if (event?.keyCode === 13) {
        setTimeout(() => {
          form.submit();
        }, 0);
      }
    };
    document.addEventListener('keydown', listener);

    return () => document.removeEventListener('keydown', listener);
  }, []);

  return null;
}

function validateRequired({ message } = {}) {
  return (value) => {
    if (!value) return message ?? ERROR_MESSAGES.required;
    return undefined;
  };
}

function CreateNewPage() {
  const { validate, countries } = useFunctions();
  const { editingBlockState, resetEditingBlockState, mountEl, handleCreateNewPage } = useEditorContext();
  const pageTypeOptions = useSelector(pageTypeOptionsSelector);
  const filteredPageTypeOptions = useMemo(
    () => pageTypeOptions.filter((o) => o.value !== PAGE_TYPE_PROPERTY),
    [pageTypeOptions],
  );
  const onSnackbar = useSnackbar();
  const accountId = useSelector(accountIdSelector);
  useEscAction(resetEditingBlockState);
  const containerRef = useRef();
  const hasOnlyOneTypeToSelect =
    editingBlockState.blockType === EDITING_BLOCK_TYPES.CREATE_NEW_PROPERTY_PAGE ||
    editingBlockState.blockType === EDITING_BLOCK_TYPES.CREATE_NEW_AREA_PAGE;
  const initialType =
    editingBlockState.blockType === EDITING_BLOCK_TYPES.CREATE_NEW_PROPERTY_PAGE ? PAGE_TYPE_PROPERTY : PAGE_TYPE_AREA;

  useEffect(() => {
    Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAP);
  }, []);

  const handleCreatePage = async ({ type, name, countryId, areaId }) => {
    try {
      const countryName = countries?.find((country) => country.value === countryId)?.label;
      const handle = generateHandleString(name);
      const { lat, lng } = await getCoordsByAddress(countryName);

      const propertyData =
        type === PAGE_TYPE_PROPERTY
          ? {
              areaId: typeof areaId === 'number' ? areaId : undefined,
              areaName: typeof areaId === 'string' ? areaId : undefined,
              areaConfirmed: typeof areaId === 'string' ? true : undefined,
              agreement: true,
              countryId,
              address: countryName,
              latitude: lat,
              longitude: lng,
            }
          : {};
      const areaData =
        type === PAGE_TYPE_AREA
          ? {
              countryId,
              address: countryName,
              latitude: lat,
              longitude: lng,
            }
          : {};
      const newPage = await api
        .post(`/acc/${accountId}/pages`, {
          customPage: true,
          forItinerary: true,
          type,
          name,
          handle,
          ...propertyData,
          ...areaData,
        })
        .then(({ data }) => data);
      onSnackbar(`Page '${newPage.name}' has been created.`);
      handleCreateNewPage(editingBlockState.blockType, { type, name, handle, id: newPage.id });
    } catch (error) {
      const handleErrorMsg = error?.errors?.source?.handle?.[0];
      if (handleErrorMsg) {
        onSnackbar(handleErrorMsg, true);
      } else {
        onSnackbar("Couldn't create page. Please try again.", true);
      }
      throw error;
    }
  };

  return createPortal(
    <Form
      onSubmit={handleCreatePage}
      initialValues={{ type: initialType, name: editingBlockState.data?.query }}
      validate={validate}
      render={({ handleSubmit, submitting, values }) => {
        return (
          <OutsideClickHandler onOutsideClick={values.name ? () => handleSubmit() : () => resetEditingBlockState()}>
            <div
              className={styles.container}
              ref={containerRef}
              style={getNormalizedPositionStyles(editingBlockState.blockRect, mountEl, containerRef.current)}
            >
              <form onSubmit={handleSubmit}>
                <Field
                  component={FormGroupAdapter}
                  name="name"
                  placeholder="Page Name"
                  type="text"
                  label="What's the Page Title?"
                  onInput={capitalizeAllWordsForInput}
                />
                <Field
                  component={FormGroupAdapter}
                  name="type"
                  placeholder="Area Page"
                  type="select"
                  options={filteredPageTypeOptions}
                  label="What's the Page Type?"
                  readOnly={hasOnlyOneTypeToSelect}
                />
                {(values.type === PAGE_TYPE_AREA || values.type === PAGE_TYPE_PROPERTY) && (
                  <Field
                    component={FormGroupAdapter}
                    name="countryId"
                    placeholder="Kenya"
                    type="select"
                    label="Country"
                    options={countries}
                    validate={validateRequired()}
                  />
                )}

                <EnterHandler />
              </form>
              <MenuActionsHelper
                actions={[MenuActionsHelper.ACTION_TYPES.SELECT, MenuActionsHelper.ACTION_TYPES.ESCAPE]}
              />
              {submitting && (
                <div className={styles.loaderContainer}>
                  <Loader />
                </div>
              )}
            </div>
          </OutsideClickHandler>
        );
      }}
    />,
    mountEl,
  );
}

function NewPageContainer() {
  const { editingBlockState } = useEditorContext();

  if (
    !editingBlockState.isOpen ||
    ![
      EDITING_BLOCK_TYPES.CREATE_NEW_PAGE,
      EDITING_BLOCK_TYPES.CREATE_NEW_AREA_PAGE,
      EDITING_BLOCK_TYPES.CREATE_NEW_PROPERTY_PAGE,
    ].includes(editingBlockState.blockType)
  ) {
    return null;
  }

  return <CreateNewPage />;
}

export default NewPageContainer;
